import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import MainLayout from '../../components/layouts/MainLayout';
import PublicLayout from '../../components/layouts/PublicLayout';
import ProductGrid from '../../components/products/ProductGrid';
import Pagination from '../../components/common/Pagination';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FollowButton from '../../components/seller/FollowButton';
import { followService } from '../../services/follow.service';
import { useProducts } from '../../hooks/useProducts';
import { theme } from '../../config/theme';
import { ArrowLeftIcon, UsersIcon, PackageIcon } from '../../components/icons/Icons';

const SellerProfile = () => {
  const { sellerId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [seller, setSeller] = useState(null);
  const [followersCount, setFollowersCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const { products, pagination, loading: productsLoading, goToPage } = useProducts(
    { seller_id: sellerId },
  );

  useEffect(() => {
    fetchSellerInfo();
  }, [sellerId]);

  const fetchSellerInfo = async () => {
    setLoading(true);
    try {
      // Fetch followers count (publicly accessible via auth)
      const countRes = await followService.getFollowersCount(sellerId);
      setFollowersCount(countRes.data?.count ?? 0);

      // Extract seller info from first product if available
      // (no dedicated public seller endpoint yet)
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  };

  // Once products load, extract seller info from first product
  useEffect(() => {
    if (products.length > 0 && !seller) {
      setSeller(products[0]?.seller ?? null);
    }
  }, [products]);

  if (loading && productsLoading && !seller) return <LoadingSpinner fullScreen />;

  const content = (
    <div style={{ padding: theme.spacing.xl, maxWidth: '1200px', margin: '0 auto' }}>
      <Button
        variant="ghost"
        onClick={() => navigate(-1)}
        style={{ marginBottom: theme.spacing.lg }}
      >
        <ArrowLeftIcon size={18} />
        Back
      </Button>

      {/* Seller Header */}
      <div style={{
        backgroundColor: theme.colors.white,
        borderRadius: theme.borderRadius.xl,
        border: `1px solid ${theme.colors.gray[200]}`,
        padding: theme.spacing.xl,
        marginBottom: theme.spacing.xl,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing.lg,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
          {/* Store Avatar */}
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: theme.borderRadius.xl,
            backgroundColor: theme.colors.primary.main,
            color: theme.colors.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: theme.typography.fontSize['2xl'],
            fontWeight: theme.typography.fontWeight.bold,
            flexShrink: 0,
          }}>
            {seller?.store_name?.[0]?.toUpperCase() ?? '?'}
          </div>

          <div>
            <h1 style={{
              fontSize: theme.typography.fontSize['2xl'],
              fontWeight: theme.typography.fontWeight.bold,
              marginBottom: theme.spacing.xs,
            }}>
              {seller?.store_name ?? 'Loading...'}
            </h1>
            <div style={{
              display: 'flex',
              gap: theme.spacing.lg,
              fontSize: theme.typography.fontSize.sm,
              color: theme.colors.text.secondary,
            }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                <PackageIcon size={14} />
                {pagination?.total ?? 0} Products
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                <UsersIcon size={14} />
                {followersCount} Followers
              </span>
            </div>
          </div>
        </div>

        <FollowButton sellerId={sellerId} />
      </div>

      {/* Products */}
      <h2 style={{
        fontSize: theme.typography.fontSize.xl,
        fontWeight: theme.typography.fontWeight.semibold,
        marginBottom: theme.spacing.lg,
      }}>
        Products
      </h2>

      <ProductGrid
        products={products}
        loading={productsLoading}
        onClick={(product) => navigate(`/products/${product.slug}`)}
      />

      <Pagination
        currentPage={pagination?.page}
        totalPages={pagination?.totalPages}
        hasNextPage={pagination?.hasNextPage}
        hasPreviousPage={pagination?.hasPreviousPage}
        totalItems={pagination?.total}
        itemsPerPage={pagination?.limit}
        onPageChange={goToPage}
      />
    </div>
  );

  return isAuthenticated ? (
    <MainLayout>{content}</MainLayout>
  ) : (
    <PublicLayout>{content}</PublicLayout>
  );
};

export default SellerProfile;